import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { styled, lighten, darken } from '@mui/system';
import { ApiCommunitySearchPark, ApiCommunityClaimSelectedPlace } from '@/api/community/verify-place';
import Cookies from 'universal-cookie';
import { Controller, FieldError, UseFormSetValue } from 'react-hook-form';
import { ICommunityClaimFormData } from '@/interfaces/community/verify-place';
import { PlaceAllSearch } from '@/interfaces/community/article';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'light'
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled('ul')({
  padding: 0,
});

type Props = {
  control: any;
  error?: FieldError;
  setValue: UseFormSetValue<ICommunityClaimFormData>;
};

const AutoCompletePlace: React.FC<Props> = ({ control, error, setValue }) => {

  const { query } = useRouter();
  const { placeId } = query;

  const [searchResults, setSearchResults] = React.useState<PlaceAllSearch[]>([]);

  const handleInputChange = (event: any, value: string) => {
    ApiCommunitySearchPark(1, value, '', '', 0, 30, 'claim-search')
      .then(results => setSearchResults(results.data.response))
      .catch(err => console.error('Error fetching search results.', err));
  };

  const options = searchResults.map(place => ({
    firstLetter: /[0-9]/.test(place.placeName.trim()[0]) ? '0-9' : place.placeName.trim()[0].toUpperCase(),
    ...place,
    placeName: place.placeName.trim(),
  }));

  useEffect(() => {
    if (placeId) {
      ApiCommunityClaimSelectedPlace(placeId)
        .then(response => {
          const placeData = response.data;
          setValue("selectedPlace", placeData);
        })
        .catch(err => console.error('Error claiming selected place:', err));
    }
  }, [placeId, setValue]);

  const handleSelectionChange = (event: any, newValue: any) => {
    if (newValue?.placeId) {
      ApiCommunityClaimSelectedPlace(newValue.placeId)
        .then(response => {
          const placeData = response.data;
          setValue("selectedPlace", placeData);
        })
        .catch(err => console.error('Error claiming selected place:', err));
    } else {
      setValue("selectedPlace", null);
    }
  };

  return (
    <Controller
      name="selectedPlace"
      control={control}
      defaultValue={null}
      rules={{ required: 'กรุณากรอกชื่อบริษัท / บริการ' }}
      render={({ field }) => (
        <Autocomplete
          id="grouped-demo"
          options={options.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
          groupBy={(option) => option.firstLetter}
          getOptionLabel={(option) => option.placeName}
          onInputChange={handleInputChange}
          value={field.value}
          onChange={handleSelectionChange}
          sx={{ width: "100%" }}
          noOptionsText="ไม่พบข้อมูลที่ค้นหา"          
          renderInput={(params) => (
            <TextField
              {...params}
              label="ชื่อบริการ / บริษัท"
              error={!!error}
              onClick={() => {
                handleInputChange(null,'');
              }}
              helperText={error ? error.message : ''}
            />
          )}
          renderGroup={(params) => (
            <li key={params.key}>
              <GroupHeader>{params.group}</GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
        />
      )}
    />
  );
};

export default AutoCompletePlace;
